import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Clock, ExternalLink, Loader2, Rocket } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useCheckout } from "@/context/CheckoutContext";
import { Picker } from "@/components/iv/scorecard/ScorecardStage";
import { Customizer } from "@/components/iv/concept/Customizer";
import { PreviewSite } from "@/components/iv/concept/PreviewSite";
import { CostAnchor } from "@/components/iv/concept/CostAnchor";
import { DfyStatus } from "@/components/iv/concept/DfyStatus";
import { usePurchases } from "@/lib/purchases";

const needsPivot = (ev) => (ev?.final_score ?? 0) <= 50 || ev?.fatal_flaw_override || (ev?.fatal_flaws?.length ?? 0) > 0;
const REFERENCE = { never_expires: true, reason: "reference", expired: false };

const useStage4Source = () => {
  const { user, authChecked, activeSlug, reports } = useApp();
  const [params] = useSearchParams();
  const [mine, setMine] = useState([]);
  const [selected, setSelected] = useState(params.get("evaluation"));
  const [own, setOwn] = useState(null);
  useEffect(() => {
    if (!authChecked) return;
    (user ? api.get("/evaluations").then((r) => r.data).catch(() => []) : Promise.resolve([])).then((list) => {
      setMine(list);
      setSelected((cur) => cur || (list.find((e) => e.status === "completed")?.id ?? `case:${activeSlug}`));
    });
  }, [authChecked, user]); // eslint-disable-line react-hooks/exhaustive-deps
  const lastSlug = useRef(activeSlug);
  useEffect(() => { // header case switcher jumps to that reference preview (same as Stage 03)
    if (activeSlug !== lastSlug.current) { lastSlug.current = activeSlug; setSelected(`case:${activeSlug}`); }
  }, [activeSlug]);
  const isCase = !selected || selected.startsWith("case:");
  useEffect(() => {
    setOwn(null);
    if (isCase) return undefined;
    let alive = true, timer;
    const poll = () => api.get(`/evaluations/${selected}/preview`).then(({ data }) => {
      if (!alive) return;
      setOwn({ ...data, forId: selected });
      if (!["ready", "failed", "expired"].includes(data.preview_status)) timer = setTimeout(poll, 4000);
    }).catch(() => alive && setOwn({ preview_status: "failed", forId: selected }));
    poll();
    return () => { alive = false; clearTimeout(timer); };
  }, [selected, isCase]);
  const caseView = useMemo(() => {
    const r = isCase ? reports[selected?.slice(5) || activeSlug] : null;
    if (!r) return null;
    const pivot = needsPivot(r.evaluation);
    const preview = r.website_previews.find((p) => p.is_safe_pivot_preview === pivot) || r.website_previews[0];
    return { preview_status: "ready", preview, is_safe_pivot_preview: pivot, reservation: REFERENCE };
  }, [isCase, reports, selected, activeSlug]);
  // Only data that belongs to the CURRENT selection is ever exposed (prevents stale-state crashes on switch).
  return { mine, selected, setSelected, isCase, data: isCase ? caseView : own?.forId === selected ? own : null };
};

const Skeleton = () => (
  <div className="grid gap-4 lg:grid-cols-12" data-testid="preview-skeleton" aria-busy="true">
    <div className="h-80 animate-pulse rounded-2xl bg-white/[0.04] lg:col-span-4" />
    <div className="h-[420px] animate-pulse rounded-2xl bg-white/[0.04] lg:col-span-8" />
  </div>
);

const Expired = ({ data, evaluationId }) => {
  const { openCheckout } = useCheckout();
  return (
    <div className="iv-glass space-y-3 rounded-2xl p-6" data-testid="preview-expired">
      <p className="flex items-center gap-2 text-lg font-bold text-white"><Clock className="h-5 w-5 text-amber-300" aria-hidden="true" /> {data.message || "This preview has expired — your report is still in your workspace"}</p>
      <p className="text-sm text-slate-400">{data.preview?.subdomain_slug}.preview.ideavalidator360.com</p>
      <div className="flex flex-wrap gap-2">
        <a href={`/?stage=3&evaluation=${evaluationId}`} className="rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-white" data-testid="preview-expired-report-link">Open my report</a>
        <button type="button" onClick={() => openCheckout("DFY_WEBSITE_LAUNCH", { evaluationId })} className="iv-cta inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold text-white" data-testid="preview-expired-launch">
          <Rocket className="h-4 w-4" aria-hidden="true" /> Claim Custom Domain Launch
        </button>
      </div>
    </div>
  );
};

const Canvas = ({ preview, spec, mobile, pivot }) => (
  <div className="min-w-0 space-y-2 lg:col-span-8">
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#0B1021]" data-testid="preview-browser-frame">
      <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2">
        <span className="flex gap-1" aria-hidden="true"><i className="h-2.5 w-2.5 rounded-full bg-rose-400/70" /><i className="h-2.5 w-2.5 rounded-full bg-amber-400/70" /><i className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" /></span>
        <span className="min-w-0 flex-1 truncate rounded-full bg-white/5 px-3 py-1 font-mono text-[11px] text-slate-400" data-testid="preview-url">{preview.subdomain_slug}.preview.ideavalidator360.com</span>
        <a href={`/preview/${preview.subdomain_slug}`} target="_blank" rel="noreferrer" className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-cyan-300" data-testid="preview-open-standalone">Open <ExternalLink className="h-3 w-3" /></a>
      </div>
      <div className={`noscrollbar max-h-[720px] overflow-y-auto ${mobile ? "mx-auto max-w-[360px] border-x border-white/10" : ""}`} data-testid="preview-canvas" data-viewport={mobile ? "mobile" : "desktop"}>
        <PreviewSite spec={spec} imageUrl={preview.mockup_image_url} mobile={mobile} />
      </div>
    </div>
    <p className="inline-flex rounded-full border border-cyan-400/40 px-3 py-1 text-[11px] font-semibold text-cyan-300" data-testid="preview-live-pill">
      ● Live Test Preview (Subdomain){pivot ? " · Recommended Safe Asset-Light Pivot" : ""}
    </p>
  </div>
);

export const ConceptStage = () => {
  const { mine, selected, setSelected, isCase, data } = useStage4Source();
  const [edit, setEdit] = useState(null);
  const [mobile, setMobile] = useState(false);
  const preview = data?.preview_status === "ready" ? data.preview : null;
  useEffect(() => { setEdit(preview ? { slug: preview.subdomain_slug, spec: preview.site_spec_json } : null); }, [preview]);
  const spec = preview && edit?.slug === preview.subdomain_slug ? edit.spec : null;
  const setSpec = useCallback((next) => setEdit((e) => (e ? { ...e, spec: next } : e)), []);
  const status = data?.preview_status;
  const owned = usePurchases(isCase ? null : selected);
  return (
    <section className="space-y-8 pb-12 pt-6" aria-labelledby="concept-h1" data-testid="concept-stage">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 id="concept-h1" className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl">3D Concept & Test Website</h1>
          <p className="mt-2 text-sm text-slate-400">Free for every validated idea. {isCase ? "Reference case study preview." : "Your private preview — shareable only via its unguessable link."}</p>
        </div>
        <Picker mine={mine.filter((e) => e.status === "completed")} value={selected} onChange={setSelected} />
      </div>
      {!data && <Skeleton />}
      {data && !["ready", "failed", "expired"].includes(status) && (
        <p className="iv-glass flex items-center gap-2 rounded-2xl p-5 text-sm text-slate-300" data-testid="preview-generating">
          <Loader2 className="h-4 w-4 animate-spin text-cyan-300" /> Generating your 3D concept and test website{data.is_safe_pivot_preview ? " (Recommended Safe Asset-Light Pivot)" : ""}…
        </p>
      )}
      {status === "failed" && <p className="rounded-xl border border-rose-400/40 bg-rose-500/10 p-4 text-sm text-rose-200" data-testid="preview-failed">Preview generation failed. Reload this page to retry.</p>}
      {status === "expired" && <Expired data={data} evaluationId={selected} />}
      {status === "ready" && !spec && <Skeleton />}
      {spec && (
        <div className="grid gap-4 lg:grid-cols-12">
          <Customizer spec={spec} setSpec={setSpec} slug={preview.subdomain_slug} canEdit={!isCase} mobile={mobile} setMobile={setMobile} />
          <Canvas preview={preview} spec={spec} mobile={mobile} pivot={data.is_safe_pivot_preview} />
        </div>
      )}
      <DfyStatus deployment={owned?.deployment} />
      <CostAnchor slug={data?.preview?.subdomain_slug} reservation={data?.reservation} evaluationId={isCase ? undefined : selected} />
    </section>
  );
};
